/**
 * Provedor de sessão do front.
 *
 * ATENÇÃO (CONSTITUICAO.md §4.1): nada aqui autoriza coisa nenhuma. Este
 * provedor existe para o front SABER o que mostrar. Quem decide é o porteiro,
 * no servidor, a cada requisição.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { api, ErroApi, registrarPerdaDeSessao } from './api'
import { Contexto } from './contexto-sessao'
import type { ContextoSessao, EstadoSessao, Sessao } from './contexto-sessao'

export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const [situacao, setSituacao] = useState<EstadoSessao>({ estado: 'carregando' })

  const limpar = useCallback(() => setSituacao({ estado: 'anonimo' }), [])

  const revalidar = useCallback(async () => {
    try {
      const sessao = await api.get<Sessao>('/eu')
      setSituacao({ estado: 'autenticado', sessao })
    } catch (erro) {
      if (erro instanceof ErroApi && (erro.sessaoMorreu || erro.status === 401)) {
        setSituacao({ estado: 'anonimo' })
        return
      }
      // Erro de rede não é logout: derrubar a sessão por causa de um wi-fi
      // instável faria o caixa refazer o login no meio do movimento.
      setSituacao((atual) => (atual.estado === 'carregando' ? { estado: 'anonimo' } : atual))
    }
  }, [])

  const sair = useCallback(async () => {
    try {
      await api.delete('/sessoes/atual')
    } finally {
      setSituacao({ estado: 'anonimo' })
    }
  }, [])

  useEffect(() => {
    // Qualquer requisição que receba "sessão inválida" derruba o estado local,
    // mesmo que a tela não estivesse esperando por isso.
    registrarPerdaDeSessao(() => setSituacao({ estado: 'anonimo' }))
    void revalidar()
  }, [revalidar])

  const valor = useMemo<ContextoSessao>(
    () => ({
      situacao,
      definir: (sessao) => setSituacao({ estado: 'autenticado', sessao }),
      limpar,
      revalidar,
      sair,
      nivelDe: (permissao) =>
        situacao.estado === 'autenticado'
          ? (situacao.sessao.niveis[permissao] ?? 'none')
          : 'none',
      pode: (permissao) =>
        situacao.estado === 'autenticado' && situacao.sessao.niveis[permissao] === 'full',
      exigePIN: (permissao) =>
        situacao.estado === 'autenticado' && situacao.sessao.niveis[permissao] === 'pin',
      podeVer: (permissao) =>
        situacao.estado === 'autenticado' && situacao.sessao.niveis[permissao] !== undefined,
    }),
    [situacao, limpar, revalidar, sair],
  )

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}
